import {SpeedController} from '../worklet/SpeedController';
import {Util} from '../util/Util';
import {AnimFrame, AnimFrameEvent, AnimFrameEventType} from 'alm_animframe';
import {Easing, NumUtil} from 'alm_coreutil';
import {Task} from 'alm-tasker.js/dist/core/Task';
import * as tasker from 'tasker';
import {SimpleMovingAverage} from '../util/SimpleMovingAverage';
import {Tween} from 'alm-tasker.js/dist/task/Tween';

/**
 - OSX Chrome
 ユーザージェスチャー後に new AudioContext() をおこなわないと鳴らない
 上記を満たせばstartはプログラムのタイミングでで実行可能

 - iOS Safari
 ユーザージェスチャー前でも new AudioContext() は可能
 startのタイミングでユーザージェスチャーが必要
 httpsでないと鳴らない

 - Android Chrome
 pointerdownのユーザージェスチャーではコンテキストを生成できない
 clickなら可
 */

export class Application {
	// --------------------------------------------------
	//
	// CONSTRUCTOR
	//
	// --------------------------------------------------

	constructor() {
		this.discElement = document.getElementById('disc');

		// ピンチジェスチャーを無効化する
		document.addEventListener('touchstart', (event:TouchEvent):void => {
			if (event.touches.length > 1) {
				event.preventDefault();
			}
		}, { passive: false });

		// デバイス判別
		this.isIos = false;
		this.isAndroid = false;
		this.isPc = false;
		const ua = navigator.userAgent.toLowerCase();
		if ((ua.indexOf('iphone') !== -1) || (ua.indexOf('ipad') !== -1)) {
			// iOS
			this.isIos = true;
			console.log('iOS');
		} else if (ua.indexOf('android') > 0) {
			// Android
			this.isAndroid = true;
			console.log('Android');
		} else {
			// PC
			this.isPc = true;
			console.log('PC');
		}

		this.isTouching = false;
		this.touchId = -1;

		this.startButton = document.getElementById('start-button');
		this.blinkStartButton();

		// 初期化用ジェスチャ
		if (this.isAndroid) {
			this.discElement.addEventListener('click', this.initGestureHandler);
		} else {
			this.discElement.addEventListener('pointerdown', this.initGestureHandler);
		}
	}

	// --------------------------------------------------
	//
	// METHOD
	//
	// --------------------------------------------------

	private load(): void {
		// show loading
		this.startButton.innerHTML = 'LOADING..';
		this.blinkLoading();

		// iOSでミュートの場合はWebAudioが鳴らない
		// iOSでミュートでも音がなるようにダミーのaudioタグを作成する
		const audio = new Audio();
		audio.src = 'static/audio/none.mp3';
		audio.load();
		document.body.appendChild(audio);

		// create context
		this.audioContext = new (AudioContext || window['webkitAudioContext'])();

		// get permission
		const node = this.audioContext.createOscillator();
		node.connect(this.audioContext.destination);
		node.start();
		node.stop();
		node.disconnect();

		// load worklet module
		Util.addAudioWorklet(this.audioContext, SpeedController).then((): void => {
			const audioUrl = 'static/audio/62loop.wav';
			//const audioUrl = 'static/audio/bit167-1.mp3';
			//const audioUrl = 'static/audio/relax_020.mp3';

			// load audio
			fetch(audioUrl)
				.then((response) => response.arrayBuffer())
				.then((buffer) => this.audioContext.decodeAudioData(buffer))
				.then((buffer) => {
					tasker
						.serial(
							tasker.delay(1000),
							tasker.call((): void => {
								// hide start button
								this.startButtonBlinkTween.dispose();

								// play dummy audio
								audio.play();

								tasker
									.tween(300, Easing.easeOutQuad)
									.target(this.startButton.style, 'opacity', 0, { from: 0.7 })
									.onComplete((): void => {
										this.startButton.style.setProperty('display', 'none');
									})
									.run();

								// volume control
								this.defaultVolume = 0.1;
								if (this.isIos) {
									this.defaultVolume = 0.15;
								} else if (this.isAndroid) {
									this.defaultVolume = 0.5;
								}

								this.gainNode = this.audioContext.createGain();
								this.gainNode.connect(this.audioContext.destination);
								this.gainNode.gain.value = this.defaultVolume;

								//const node = this.audioContext.createBiquadFilter();
								//node.type = 'highpass';
								//node.frequency.value = 300;
								//node.connect(this.gainNode);

								// speed control
								const samples: Float32Array[] = [];
								for (let i = 0; i < buffer.numberOfChannels; ++i) {
									samples.push(buffer.getChannelData(i));
								}

								this.speedControlNode = new AudioWorkletNode(this.audioContext, 'SpeedController', { processorOptions: { samples: samples } });
								//this.speedControlNode.connect(node);
								this.speedControlNode.connect(this.gainNode);
								this.speedControlVelocity = this.speedControlNode.parameters.get('velocity');

								this.currentIntensity = 0;
								this.currentIntensityAverage = new SimpleMovingAverage(10);
								this.speedControlNode.port.onmessage = (event) => {
									this.currentIntensityAverage.add(event.data.intensity);
									//console.log('sample count :', event.data.sampleCount);
								};

								this.discElement.classList.add('playing');
								this.discElement.addEventListener('pointerdown', this.pointerDownHandler);
								this.discGraphic = <HTMLElement>this.discElement.getElementsByClassName('graphic').item(0);

								this.animFrameTime = 0;
								this.animFrame = new AnimFrame(60);
								this.animFrame.addEventListener(AnimFrameEventType.tick, this.animFrameTickHandler);
								this.animFrame.start();

								this.trackVelocity = 1; // 8
								this.trackTargetVelocity = 1;
								this.trackTargetVelocityEasing = 0.01;
								this.trackTargetVelocityEasingDefault = 0.2;
								this.trackVelocityDefault = 1;

								this.pointerAngle = this.pointerAnglePrev = 0;
								this.pointerAngleVelocity = 0;

								this.discAngle = this.discAnglePrev = 0;
								this.discAngleVelocity = 0.01;
								this.discAngleCoefficient = 0.02; //0.005;

								this.dotScale = 1;
								this.dotScaleChangedTime = -1;
								this.isDotScalesUp = true;
								this.isDotScalesUpNext = true;
								this.discAngleSpeedSmoothed = 0;

								this.trackVelocity = 0;
								this.trackTargetVelocity = 0.5;
								this.trackTargetVelocityTween = <Task>(<unknown>tasker.serial(tasker.tween(200, Easing.linear).target(this, 'trackTargetVelocity', 25), tasker.tween(1200, Easing.easeOutSine).target(this, 'trackTargetVelocity', this.trackVelocityDefault)));
								this.trackTargetVelocityTween.run();

								this.trackTargetVelocityEasingTween?.dispose();
								this.trackTargetVelocityEasingTween = <Task>(<unknown>tasker.tween(3500, Easing.easeInSine).target(this, 'trackTargetVelocityEasing', 1));
								this.trackTargetVelocityEasingTween.run();

								this.apply();
							}),
						)
						.run();
				});
		});
	}

	private update(): void {
		if (this.isTouching) {
			// 毎フレームのディスクの回転角度を割り出す
			this.pointerAngleVelocity = NumUtil.turn(this.pointerAnglePrev % NumUtil.PI2, this.pointerAngle % NumUtil.PI2);
			this.discAngle += this.pointerAngleVelocity;
			this.discAngleVelocity = NumUtil.turn(this.discAnglePrev % NumUtil.PI2, this.discAngle % NumUtil.PI2);

			// ディスクの中心付近だとドラッグの動きで角度が変わりすぎるので弱める
			const targetEasing = NumUtil.map(this.pointerRadius / this.discRadius, 0, 0.5, 0.01, 1);

			// ディスクの回転角度を再生速度に変換する
			this.trackTargetVelocity = this.discAngleVelocity / this.discAngleCoefficient;
			this.trackVelocity += (this.trackTargetVelocity - this.trackVelocity) * targetEasing;

			const discAngleSpeed = Math.abs(this.discAngleVelocity);
			this.discAngleSpeedSmoothed += (discAngleSpeed - this.discAngleSpeedSmoothed) * 0.3;
			this.scaleDot(this.discAngleSpeedSmoothed > 0.01);

			//const discAngleSpeed = Math.abs(this.discAngleVelocity);
			//this.scaleDot(discAngleSpeed > 0.01);

			//const discAngleSpeed = Math.abs(this.discAngleVelocity);
			//this.scaleDot(discAngleSpeed > 0);

			/*
			const discAngleSpeed = Math.abs(this.discAngleVelocity);
			const isDotScaleUpTarget = discAngleSpeed > 0;
			if (this.isDotScalesUp !== isDotScaleUpTarget) {

				if (isDotScaleUpTarget) {
					this.isDotScalesUpNext = true;
				}

				const t = DateUtil.now();
				if (t - this.dotScaleChangedTime > 100) {

					if (isDotScaleUpTarget) {
						this.dotScaleChangedTime = t;
						this.scaleDot(isDotScaleUpTarget);
					}

					if (!isDotScaleUpTarget && !this.isDotScalesUpNext) {
						this.dotScaleChangedTime = t;
						this.scaleDot(isDotScaleUpTarget);
						this.isDotScalesUpNext = false;
					}
				}
			}
			*/
		} else {
			// 再生速度を更新する
			//this.trackTargetVelocity = 1;
			this.trackVelocity += (this.trackTargetVelocity - this.trackVelocity) * this.trackTargetVelocityEasing;

			// 再生速度をディスクの回転角度に変換する
			this.discAngle += this.trackVelocity * this.discAngleCoefficient;
			this.discAngleVelocity = NumUtil.turn(this.discAnglePrev % NumUtil.PI2, this.discAngle % NumUtil.PI2);
		}

		// 現在のフレームの情報を保存する
		this.pointerAnglePrev = this.pointerAngle;
		this.discAnglePrev = this.discAngle;

		//const t = DateUtil.now();
		//console.log('fps :', Math.floor(1000 / (t - this.animFrameTime)));
		//this.animFrameTime = DateUtil.now();
	}

	private apply(): void {
		this.gainNode.gain.value = NumUtil.map(Math.abs(this.trackVelocity), 1, 10, this.defaultVolume, this.defaultVolume * 0.5);

		const average = NumUtil.ease(this.currentIntensityAverage.value, 0.2, 0.5, 0, 1, Easing.easeInOutQuad);
		if (average > this.currentIntensity) {
			this.currentIntensity = average;
		}
		this.currentIntensity -= 0.05;
		if (this.currentIntensity < 0) {
			this.currentIntensity = 0;
		}

		document.documentElement.style.setProperty('--intensity', this.currentIntensity.toString());
		//console.log(this.currentIntensity);

		this.speedControlVelocity.value = this.trackVelocity;
		this.discGraphic.style.setProperty('transform', `translate(-50%, -50%) rotate(${this.discAngle}rad)`);
	}

	private blinkStartButton(): void {
		this.startButtonBlinkTween?.dispose();
		this.startButtonBlinkTween = <Task>(<unknown>tasker.serial(
			tasker.tween(1500, Easing.easeInQuad).target(this.startButton.style, 'opacity', 0.3, { from: 0.7 }),
			tasker.call(() => {
				this.blinkStartButton();
			}),
		));
		this.startButtonBlinkTween.run();
	}

	private blinkLoading(): void {
		this.startButtonBlinkTween?.dispose();
		this.startButtonBlinkTween = <Task>(<unknown>tasker.serial(
			tasker.tween(100, Easing.linear).target(this.startButton.style, 'opacity', 0.3, { from: 0.7 }),
			tasker.call(() => {
				this.blinkLoading();
			}),
		));
		this.startButtonBlinkTween.run();
	}

	private initGestureHandler = (event: PointerEvent): void => {
		this.discElement.removeEventListener('click', this.initGestureHandler);
		this.discElement.removeEventListener('pointerdown', this.initGestureHandler);
		this.load();
	};

	private animFrameTickHandler = (event: AnimFrameEvent): void => {
		this.update();
		this.apply();
	};

	private pointerDownHandler = (event: PointerEvent): void => {
		if (this.touchId !== -1) return;
		this.touchId = event.pointerId;

		//if (this.isTouching) return;
		this.isTouching = true;

		window.addEventListener('pointermove', this.pointerMoveHandler);
		window.addEventListener('pointerup', this.pointerUpHandler);

		this.discCenterX = this.discElement.offsetLeft;
		this.discCenterY = this.discElement.offsetTop;
		this.discRadius = window.innerWidth * 0.9 * 0.5;

		this.pointerPositionX = event.clientX;
		this.pointerPositionY = event.clientY;

		const dx = this.pointerPositionX - this.discCenterX;
		const dy = this.pointerPositionY - this.discCenterY;
		this.pointerRadius = Math.sqrt(dx * dx + dy * dy);
		this.pointerAngle = Math.atan2(dy, dx);
		this.pointerAnglePrev = this.pointerAngle;

		this.trackTargetVelocityEasingTween?.dispose();
		this.trackTargetVelocityEasingTween = <Task>(<unknown>tasker.tween(500, Easing.easeInSine).target(this, 'trackTargetVelocityEasing', 1));
		this.trackTargetVelocityEasingTween.run();

		this.discAngleSpeedSmoothed = 0;
		this.trackTargetVelocityTween?.dispose();

		this.scaleDot(false);
	};

	private pointerMoveHandler = (event: PointerEvent): void => {
		if (this.touchId !== event.pointerId) return;

		//if (!this.isTouching) return;

		this.pointerPositionX = event.clientX;
		this.pointerPositionY = event.clientY;

		const dx = this.pointerPositionX - this.discCenterX;
		const dy = this.pointerPositionY - this.discCenterY;
		this.pointerRadius = Math.sqrt(dx * dx + dy * dy);
		this.pointerAngle = Math.atan2(this.pointerPositionY - this.discCenterY, this.pointerPositionX - this.discCenterX);
	};

	private pointerUpHandler = (event: PointerEvent): void => {
		if (this.touchId !== event.pointerId) return;
		this.touchId = -1;

		//if (!this.isTouching) return;
		this.isTouching = false;

		window.removeEventListener('pointermove', this.pointerMoveHandler);
		window.removeEventListener('pointerup', this.pointerUpHandler);

		this.trackVelocity = this.discAngleVelocity / this.discAngleCoefficient;
		const trackSpeed = Math.abs(this.trackVelocity);

		const throwSpeed = Math.min(5, trackSpeed);
		this.trackTargetVelocityEasing = NumUtil.map(throwSpeed, 0, 10, this.trackTargetVelocityEasingDefault * 0.5, 0.02);
		this.trackTargetVelocityEasingTween?.dispose();
		this.trackTargetVelocityEasingTween = <Task>(<unknown>tasker.tween(1500, Easing.easeInSine).target(this, 'trackTargetVelocityEasing', 1));
		this.trackTargetVelocityEasingTween.run();

		this.trackTargetVelocityTween?.dispose();
		if (trackSpeed < 1.0) {
			this.trackTargetVelocity = (this.trackVelocity >= 0 ? 1 : -1) * this.trackVelocityDefault * 0.1;
			this.trackTargetVelocityTween = <Task>(<unknown>tasker.tween(500, Easing.linear).target(this, 'trackTargetVelocity', this.trackVelocityDefault));
			this.trackTargetVelocityTween.run();
		} else {
			this.trackTargetVelocity = this.trackVelocityDefault;
		}

		this.scaleDot(true);
	};

	private scaleDot(isScaleUp: boolean): void {
		if (this.isDotScalesUp === isScaleUp) return;
		this.isDotScalesUp = isScaleUp;

		const targetScale = isScaleUp ? 1 : 0.5;
		const duration = isScaleUp ? 300 : 100;

		this.dotScaleTween?.dispose();
		this.dotScaleTween = (<Tween>(<unknown>tasker.tween(duration, Easing.easeOutQuad).target(this, 'dotScale', targetScale))).onUpdate(() => {
			document.documentElement.style.setProperty('--dot-scale', this.dotScale.toString());
		});
		this.dotScaleTween.run();
	}

	// --------------------------------------------------
	//
	// MEMBER
	//
	// --------------------------------------------------

	private audioContext: AudioContext;
	private gainNode: GainNode;
	private speedControlNode: any;
	private speedControlVelocity: any;
	private defaultVolume: number;

	private animFrame: AnimFrame;
	private animFrameTime: number;

	private trackVelocity: number;
	private trackTargetVelocity: number;
	private trackTargetVelocityEasing: number;
	private trackTargetVelocityEasingDefault: number;
	private trackTargetVelocityEasingTween: Task;
	private trackTargetVelocityTween: Task;
	private trackVelocityDefault: number;

	private currentIntensity: number;
	private currentIntensityAverage: SimpleMovingAverage;

	private discElement: HTMLElement;
	private discGraphic: HTMLElement;
	private discRadius: number;
	private discCenterX: number;
	private discCenterY: number;
	private discAngle: number;
	private discAnglePrev: number;
	private discAngleVelocity: number;
	private discAngleCoefficient: number;
	private discAngleSpeedSmoothed: number;

	private dotScale: number;
	private dotScaleTween: Task;
	private dotScaleChangedTime: number;
	private isDotScalesUp: boolean;
	private isDotScalesUpNext: boolean;

	private pointerPositionX: number;
	private pointerPositionY: number;
	private pointerRadius: number;
	private pointerAngle: number;
	private pointerAnglePrev: number;
	private pointerAngleVelocity: number;

	private isTouching: boolean;
	private touchId:number;

	private startButton: HTMLElement;
	private startButtonBlinkTween: Task;

	private isIos: boolean;
	private isAndroid: boolean;
	private isPc: boolean;
}
